import * as googleAPI from "./google-api";
import {
    AuthenticationFailure,
    isThirdPartyCookieError,
    ensureGoogleAccessTokenIsPresent,
    GoogleAccessTokenFailureKind,
} from "./firebase-auth";
import { SourcePadState } from "../store/types";
import { generatePublishedAppDataFromSheet } from "./firebase-functions";
import { trackEvent } from "../analytics";
import { showErrorModal } from "designer/ui/smart/modals/modals";
import { assertNever, defined, dontAwait } from "common/Support";
import { NewAppSurveyData } from "common/firebase-function-types";

type SheetSourceSignInFailure = Readonly<{
    kind: "sheet-source-sign-in-failure";
    underlying: AuthenticationFailure;
}>;

// FIXME: underlying is extremely ill-typed here.
type SheetSourceGenerateFailure = Readonly<{
    kind: "sheet-source-generate-failure";
    underlying: string;
}>;

export type SheetSourceFailure = SheetSourceSignInFailure | SheetSourceGenerateFailure;

export type SheetSourceSuccess = Readonly<{
    kind: "sheet-source-success";
    id: string;
}>;

export type SheetSourceResult = SheetSourceFailure | SheetSourceSuccess;

export function isSheetSourceSignInFailure(x: SheetSourceFailure): x is SheetSourceSignInFailure {
    return x.kind === "sheet-source-sign-in-failure";
}

export function isSheetSourceSuccess(x: SheetSourceResult): x is SheetSourceSuccess {
    return x.kind === "sheet-source-success";
}

export function isAppID(x: unknown): x is Readonly<{ appID: string }> {
    return typeof x === "object" && x !== null && "appID" in x;
}

export class GoogleSheetsPicker {
    constructor(onInitErr: (err: any) => void) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises

        googleAPI.init().catch(onInitErr);
    }

    public async chooseSheet(): Promise<SheetSourceResult | undefined | "cancel"> {
        const result = await ensureGoogleAccessTokenIsPresent();
        if (!result.success) {
            switch (result.kind) {
                case GoogleAccessTokenFailureKind.UserCanceled:
                    return "cancel";
                case GoogleAccessTokenFailureKind.SigInFailure:
                    return { kind: "sheet-source-sign-in-failure", underlying: defined(result.failure) };
                case GoogleAccessTokenFailureKind.NoAccess:
                    return undefined;
                default:
                    return assertNever(result.kind);
            }
        }
        return await this.createPicker(result.token);
    }

    private async createPicker(accessToken: string): Promise<SheetSourceResult | "cancel"> {
        const google = await googleAPI.getGoogleAsync();
        if (google === undefined) {
            return {
                kind: "sheet-source-generate-failure",
                underlying: googleAPI.googleConnectionFailureMessage,
            };
        }
        const view = new google.picker.DocsView(google.picker.ViewId.DOCS);
        view.setMode(google.picker.DocsViewMode.LIST);
        view.setMimeTypes("application/vnd.google-apps.spreadsheet");

        return await new Promise<SheetSourceResult | "cancel">(resolve => {
            const picker = new google.picker.PickerBuilder()
                .enableFeature(google.picker.Feature.NAV_HIDDEN)
                // @types/google.picker is missing SUPPORT_DRIVES but it's definitely in there.
                .enableFeature((google.picker.Feature as any).SUPPORT_DRIVES)
                .setAppId(googleAPI.getCredentials().appId)
                .setOAuthToken(accessToken)
                .addView(view)
                .setDeveloperKey(googleAPI.getCredentials().developerKey)
                .setCallback(async (data: any) => {
                    if (data.action === google.picker.Action.PICKED) {
                        const { id } = data.docs[0];
                        resolve({ kind: "sheet-source-success", id });
                    } else if (data.action === google.picker.Action.CANCEL) {
                        resolve("cancel");
                    }
                    // sometimes we get a callback where `action` isn't
                    // one of these, in which case we don't want to resolve
                    // the promise.
                })
                .build();

            picker.setVisible(true);
        });
    }
}

export class GoogleSheetsSource {
    private sheetPicker: GoogleSheetsPicker;
    constructor(
        private readonly _onSheetPicked: (
            appID: string,
            organizationID: string | undefined,
            sourcePad: SourcePadState,
            preferredFormFactor: string,
            appName: string | undefined
        ) => Promise<void>,
        onCookieError: () => void
    ) {
        this.sheetPicker = new GoogleSheetsPicker(err => {
            if (isThirdPartyCookieError(err)) {
                onCookieError();
            }
        });
    }

    public async chooseSheet(
        organizationID: string | undefined,
        newAppSurveyData: NewAppSurveyData,
        appName: string | undefined
    ): Promise<SheetSourceFailure | undefined | "cancel" | Readonly<{ appID: string }>> {
        const result = await this.sheetPicker.chooseSheet();
        if (result === "cancel") return "cancel";
        if (result === undefined || !isSheetSourceSuccess(result)) return result;
        trackEvent("picked sheet", {});
        return await this.fetchAndSelectSheet(result.id, organizationID, newAppSurveyData, appName);
    }

    private async fetchAndSelectSheet(
        sheetsFileID: string,
        organizationID: string | undefined,
        newAppSurveyData: NewAppSurveyData,
        appName: string | undefined
    ): Promise<SheetSourceGenerateFailure | Readonly<{ appID: string }>> {
        const response = await generatePublishedAppDataFromSheet(sheetsFileID, organizationID, newAppSurveyData);
        if (typeof response === "string") {
            return { kind: "sheet-source-generate-failure", underlying: response };
        }

        const { appID, sourceMetadata, spreadsheetName, schema } = response;

        await this._onSheetPicked(
            appID,
            organizationID,
            {
                sourceMetadata: [sourceMetadata],
                spreadsheetName,
                schema,
            },
            newAppSurveyData.formFactor,
            appName
        );
        return { appID };
    }
}

// Returns the sheet file ID
export async function pickGoogleSheet(): Promise<string | undefined> {
    async function errorModal() {
        await showErrorModal("Something went wrong getting the Google Sheet.");
    }

    const sheetPicker = new GoogleSheetsPicker(errorModal);
    const pickerResult = await sheetPicker.chooseSheet();
    if (pickerResult === "cancel") return undefined;
    if (pickerResult === undefined || !isSheetSourceSuccess(pickerResult)) {
        dontAwait(errorModal());
        return undefined;
    }

    return pickerResult.id;
}
